import { ProductLine } from './../model/product-line';
import { Quote } from './../model/quote';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Subscription, Observable } from 'rxjs';

import { ApplicationStateService } from '../services/application-state.service';
import { LoadingSpinnerService } from '../services/loading-spinner.service';
import { PageTitleService } from '../services/page-title.service';
import { CurrentUserService } from '../services/current-user.service';
import { AuditHistoryService } from '../services/audit-history.service';
import { QuoteRevisionService } from '../services/quote-revision.service';
import { CookieService } from 'angular2-cookie/services/cookies.service';
import { QuoteService } from '../services/quote.service';
import { QuoteInfoService } from '../services/quote-info.service';
import { HttpResponseService } from '../services/httpresponse-service';
import { ModalDialogService } from '../reusable-components/modal-dialog/modal-dialog.service';
import { QuoteRevisionInfoService } from '../services/quote-revision-info.service';

import { QuoteSummary } from '../model/quote-summary';
import { Constants } from '../model/constants';

import * as _ from 'lodash';

@Component({
  selector: 'my-quote-compare',
  template: require('./quote-compare.component.html'),
  styles: [require('./quote-compare.component.scss')]
})
export class QuoteCompareComponent implements OnInit, OnDestroy {
  quoteId: number;
  revisionCompareTitle: string = 'Compare Versions';
  quoteSummary: QuoteSummary;
  subscription: Subscription = new Subscription();

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public applicationStateService: ApplicationStateService,
    public spinner: LoadingSpinnerService,
    public pageTitleService: PageTitleService,
    public currentUserService: CurrentUserService,
    public cookieService: CookieService,
    public auditHistoryService: AuditHistoryService,
    public quoteRevisionService: QuoteRevisionService,
    public quoteService: QuoteService,
    public quoteInfoService: QuoteInfoService,
    public httpResponseService: HttpResponseService,
    public modalDialogService: ModalDialogService,
    public quoteRevisionInfoService: QuoteRevisionInfoService
  ) {
    this.route.params.subscribe(x => {
      this.quoteId = x['quoteId'];
    });

    this.pageTitleService.pageTitle.next({
      title: Constants.CmhConnectionTitle,
      subTitle: this.revisionCompareTitle
    });

    const currentUserSub = this.currentUserService.currentUser$.subscribe(cu => {
      this.applicationStateService.setCmhUser(cu);
    });
    this.subscription.add(currentUserSub);

    if (this.cookieService.get('Bearer') === undefined) {
      // bearer cookie doesn't exist therefore reroute to login
      window.location.href = '/web/#!/login/expired?returnUrl=' + window.location.pathname;
    }
  }

  ngOnInit() {
    this.spinner.showSpinner();

    // Refresh Application State, for user to navigate to other Quotes menu items
    const refreshStateFromDB = this.applicationStateService.refreshStateFromDB(this.quoteId).subscribe(
      dbQuote => {
        this.applicationStateService.setQuote(dbQuote);
        this.quoteInfoService.quote$.next(dbQuote);

        const quoteSummary = this.quoteService.getQuoteSummary(+this.quoteId, 1).subscribe(x => {
          this.quoteSummary = x;
          this.spinner.hideSpinner();
        });
        this.subscription.add(quoteSummary);

        // Get number of revisions by quoteId
        const qVersions = this.quoteRevisionService.getQuoteRevisionsByQuote(this.quoteId).subscribe(data => {
          if (data !== undefined && data !== null) {
            this.quoteRevisionInfoService._quoteRevisions$.next(data);
            // Redirect back to quotes list if less than 2 revisions
            if (data.length < 2) {
              this.spinner.hideSpinner();
              this.modalDialogService
                .showMessage({
                  headerText: 'Number of Versions',
                  message: `${this.quoteId} does not have more than 2 versions available!`
                })
                .subscribe(() => {
                  let cmhBaseUrl = this.cookieService.get('cmhBaseUrl');
                  window.location.href = `${cmhBaseUrl}#!/sales/quotes`;
                });
              return;
            } else {
              this.quoteRevisionInfoService._firstRevisionCompare$.next(data[0].RevisionNumber);
              this.quoteRevisionInfoService._secondRevisionCompare$.next(data[1].RevisionNumber);
              const revisionCompares = Observable.combineLatest(
                this.quoteRevisionInfoService._firstRevisionCompare$,
                this.quoteRevisionInfoService._secondRevisionCompare$
              ).subscribe(([firstCompare, SecondCompare]) => {
                this.spinner.showSpinner();
                /// Retrieve users for revisions
                this.auditHistoryService
                  .getRevisionUsersFromMultipleRevisions(this.quoteId, [firstCompare, SecondCompare])
                  .subscribe(([firstRevisionUsers, secondRevisionUsers]) => {
                    let combinedRevisionUsers = _.uniq([...firstRevisionUsers, ...secondRevisionUsers]);
                    this.quoteRevisionInfoService._combinedRevisionUsers$.next(combinedRevisionUsers);
                  });
                this.quoteRevisionService
                  .getQuoteRevisionAggregates(this.quoteId, [firstCompare, SecondCompare])
                  .subscribe(([firstData, secondData]) => {
                    this.combineRevisionsData(firstData, secondData);
                    if (this.quoteSummary) {
                      this.spinner.hideSpinner();
                    }
                  });
              });
              this.subscription.add(revisionCompares);
            }
          }
        });
        this.subscription.add(qVersions);
      },
      error => {
        this.spinner.hideSpinner();
        this.modalDialogService
          .showMessage({
            headerText: 'Invalid QuoteId',
            message: Constants.QuoteNotFoundMessage.replace('#quoteid#', this.quoteId.toString())
          })
          .subscribe(() => {
            let cmhBaseUrl = this.cookieService.get('cmhBaseUrl');
            window.location.href = `${cmhBaseUrl}#!/sales/quotes`;
          });
      }
    );
    this.subscription.add(refreshStateFromDB);
  }

  combineRevisionsData(firstData, secondData) {
    const fProductLine = firstData.Quote.ProductLineName;
    const fModel = firstData.Quote.ModelDesc;
    const sProductLine = secondData.Quote.ProductLineName;
    const sModel = secondData.Quote.ModelDesc;
    if (fProductLine === sProductLine && fModel === sModel) {
      this.quoteRevisionInfoService._isModelProductUpdated$.next(false);
      let uniqueOptionNumbers = _.uniqBy([...firstData.SelectedOptions, ...secondData.SelectedOptions], 'OptionNumber');
      let sortedUniqueOptionNumbers = _.sortBy(uniqueOptionNumbers, [option => +option.OptionNumber]);
      let selectedOptions = [];
      sortedUniqueOptionNumbers.forEach(d => {
        let fSelectedOption = firstData.SelectedOptions.find(fData => {
          return fData.OptionNumber === d.OptionNumber;
        });
        let sSelectedOption = secondData.SelectedOptions.find(sData => {
          return sData.OptionNumber === d.OptionNumber;
        });
        selectedOptions.push([fSelectedOption, sSelectedOption]);
      });
      this.quoteRevisionInfoService._selectedOptions$.next(selectedOptions);
    } else {
      this.quoteRevisionInfoService._isModelProductUpdated$.next(true);
    }
    this.quoteRevisionInfoService._quotes$.next([firstData.Quote, secondData.Quote]);
    this.quoteRevisionInfoService._customers$.next([firstData.Customer, secondData.Customer]);
    this.quoteRevisionInfoService._orders$.next([firstData.OrderInformation, secondData.OrderInformation]);
    this.quoteRevisionInfoService._adjustments$.next([firstData.Adjustment, secondData.Adjustment]);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./src/app/quote-compare/quote-compare.component.ts